import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

/**
 * @typedef {Object} EmergencyStoreState
 * @property {boolean} showEmergencyChat
 * @property {Record<string, { emergency: Emergency | null; messages: EmergencyMessage[]; locations: import('@/models/User/UserLocation').UserLocation[] }>} emergencyData
 * @property {import('@/models/User/UserLocation').UserLocation | null} previousEmergencyLocation
 * @property {boolean} showEmergencyListModal
 */

/**
 * @typedef {Object} EmergencyStoreActions
 * @property {(show: boolean) => void} setShowEmergencyChat
 * @property {(data: Record<string, { emergency: Emergency | null; messages: EmergencyMessage[]; locations: import('@/models/User/UserLocation').UserLocation[] }>) => void} setEmergencyData
 * @property {(location: import('@/models/User/UserLocation').UserLocation | null) => void} setPreviousEmergencyLocation
 * @property {(show: boolean) => void} setShowEmergencyListModal
 */

/** @type {import('zustand').UseBoundStore<import('zustand').StoreApi<EmergencyStoreState & EmergencyStoreActions>>} */
export const useEmergencyStore = create(
  subscribeWithSelector((set) => ({
    // State
    showEmergencyChat: false,
    emergencyData: {},
    previousEmergencyLocation: null,
    showEmergencyListModal: false,

    // Actions
    setShowEmergencyChat: (show) => set({ showEmergencyChat: show }),
    setEmergencyData: (data) => set({ emergencyData: data }),
    setPreviousEmergencyLocation: (location) =>
      set({ previousEmergencyLocation: location }),
    setShowEmergencyListModal: (show) => set({ showEmergencyListModal: show }),
  })),
);

// Selector hooks for efficient re-renders
export const useShowEmergencyChat = () =>
  useEmergencyStore((state) => state.showEmergencyChat);
export const useEmergencyData = () =>
  useEmergencyStore((state) => state.emergencyData);
export const usePreviousEmergencyLocation = () =>
  useEmergencyStore((state) => state.previousEmergencyLocation);
export const useShowEmergencyListModal = () =>
  useEmergencyStore((state) => state.showEmergencyListModal);

// Action hooks (no need for useShallow since actions don't change)
export const useEmergencyActions = () => ({
  setShowEmergencyChat: useEmergencyStore.getState().setShowEmergencyChat,
  setEmergencyData: useEmergencyStore.getState().setEmergencyData,
  setPreviousEmergencyLocation:
    useEmergencyStore.getState().setPreviousEmergencyLocation,
  setShowEmergencyListModal:
    useEmergencyStore.getState().setShowEmergencyListModal,
});
